import {createSelector} from 'reselect';
import Sections from '../../../enums/Sections';
import removeSelectKeyword from '../../../utils/removeSelectKeyword';
import safeTrim from '../../../utils/safeTrim';
import statesByField from '../../components/address/field-validation/statesByField';
import store from '../../store/store';
import snakeToKebabCase from '../../utils/snakeToKebabCase';
import getPlaceOrderBillingAddressDetails from '../BillingAddressSelectors/getPlaceOrderBillingAddressDetails';
import {getNewCardDetails} from '../CardOptionSelectors/CardOptionSelectors';
import {
  isResidentIDRequiredInAddressForm,
  isUsingDifferentBillingAddress,
} from '../DeliveryAddressSelectors/DeliveryAddressSelectors';
import {requiresResidentIdOptionSelected} from '../DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import getShouldPassNameCodeForStateIfAvailable from '../getShouldPassNameCodeForStateIfAvailable';
import getNumberOfAddresses from '../getNumberOfAddresses';
import getShouldPassNameCodeForStateIfAvailableOnPaymentResolutionPage
  from '../getShouldPassNameCodeForStateIfAvailableOnPaymentResolutionPage';
import {getArvatoPaymentData, getArvatoPersonalDetails} from '../PaymentOptionSelectors/Arvato/ArvatoSelectors';
import {
  getGooglePayBillingAddress,
  getGooglePayPaymentData,
} from '../PaymentOptionSelectors/GooglePay/GooglePaySelectors';
import {getLegacyKlarnaDetails, getStoredCV2Details} from '../PaymentOptionSelectors/PaymentOptionSelectors';
import {getUsedDeliveryAddressOnPaymentResolutionPage} from '../PaymentResolutionSelectors';
import {getSelectedPudoAddress} from '../PudoSelectors';
import {
  getDeliveryType,
  getEmailAddress,
  getSections,
  getSelectedDeliveryCountry,
  getSelectedPaymentOption,
  getSelectedResidentialAddress, getSettings,
  isGuest,
  isOptedIntoMarketing,
} from '../selectors';
import getShouldRemoveSelectKeyword from '../getShouldRemoveSelectKeyword';
import getIsPackStationOrder from './getIsPackStationOrder';

export const getPudoDeliveryAddress = createSelector(
  [getSelectedPudoAddress, getSettings],
  (selectedPudoAddress, settings) => {
    // to avoid blocking click and collect orders, trimmed fields will still ensure orders go through
    let companyName = selectedPudoAddress?.organisationName || '';
    let nameNumber = selectedPudoAddress?.buildingNameNumber || '';
  
    if (companyName?.trim()?.length > 62) {
      companyName = companyName.substring(0, 62)?.trim();
    }
    
    if (nameNumber?.trim()?.length > 32) {
      nameNumber = nameNumber.substring(0, 32)?.trim();
    }
    
    if (settings?.enable_collection) {
      return {
        addressType: 'click-and-collect',
        addressee: selectedPudoAddress.name,
        organisationName: companyName,
        buildingNameNumber: nameNumber,
        line1: selectedPudoAddress.line1,
        line2: selectedPudoAddress.line2,
        line3: selectedPudoAddress.line3,
        line4: selectedPudoAddress.line4,
        postcode: selectedPudoAddress.postcode,
        countryCode: selectedPudoAddress.countryCode,
        courierId: selectedPudoAddress.courierId,
        locationId: selectedPudoAddress.locationId,
        type: selectedPudoAddress.type,
      };
    }
    
    return {
      addressType: 'click-and-collect',
      addressee: selectedPudoAddress.name,
      organisationName: companyName,
      buildingNameNumber: nameNumber,
      line1: selectedPudoAddress.line1,
      line2: selectedPudoAddress.line2,
      line3: selectedPudoAddress.line3,
      line4: selectedPudoAddress.line4,
      postcode: selectedPudoAddress.postcode,
      countryCode: selectedPudoAddress.countryCode,
      courierId: selectedPudoAddress.courierId,
      locationId: selectedPudoAddress.locationId,
    };
  },
);

export const getResidentialDeliveryAddress = createSelector(
  [getSelectedResidentialAddress, getSections, getShouldPassNameCodeForStateIfAvailable,
    getNumberOfAddresses, getShouldRemoveSelectKeyword],
  (selectedResidentialAddress, sections,
    shouldPassNameCodeForStateIfAvailable,
    numberOfAddresses, shouldRemoveSelectKeyword) => {
    if (!selectedResidentialAddress) {
      return;
    }
    
    if (selectedResidentialAddress.addressId) {
      return {
        addressId: selectedResidentialAddress.addressId,
      };
    }
    
    let addressee = (selectedResidentialAddress.firstName && selectedResidentialAddress.lastName)
      ? `${selectedResidentialAddress.firstName} ${selectedResidentialAddress.lastName}`
      : selectedResidentialAddress.addressee;
    
    if (addressee?.trim()?.length > 34) {
      addressee = addressee.substring(0, 34);
    }
    
    const addressType = sections.includes(Sections.CONTACT) ? 'contact-details' : 'postal';
    
    const isPackstationAddress = getIsPackStationOrder(selectedResidentialAddress);
    
    let line1 = (isPackstationAddress && selectedResidentialAddress.streetName && selectedResidentialAddress.nameNumber)
      ? `${selectedResidentialAddress.streetName} ${selectedResidentialAddress.nameNumber}`
      : selectedResidentialAddress.streetName;
    
    if (isPackstationAddress && line1?.trim()?.length > 34) {
      line1 = line1.substring(0, 34);
    }
  
    const selectedState = selectedResidentialAddress.county;
    let nameCodeToSet = '';
    
    if (shouldPassNameCodeForStateIfAvailable) {
      const states = statesByField?.county(selectedResidentialAddress.country);
      const stateFound = states?.find(field => field.name === selectedState);
      if (stateFound) {
        nameCodeToSet = stateFound.nameCode;
      }
    }
    
    const line4 = (shouldPassNameCodeForStateIfAvailable && nameCodeToSet) ? nameCodeToSet : selectedResidentialAddress.county;
    const sanitizedLine4 = shouldRemoveSelectKeyword ? removeSelectKeyword(line4) : line4;
  
    const defaultDeliveryAddress = (numberOfAddresses < 1) || selectedResidentialAddress.defaultDeliveryAddress;
    const defaultBillingAddress = (numberOfAddresses < 1) || selectedResidentialAddress.defaultBillingAddress;
    
    return {
      addressType,
      addressNickname: safeTrim(selectedResidentialAddress.addressNickname),
      addressee: safeTrim(addressee),
      organisationName: safeTrim(selectedResidentialAddress.companyName),
      buildingNameNumber: safeTrim(isPackstationAddress ? selectedResidentialAddress.postalId : selectedResidentialAddress.nameNumber),
      line1: safeTrim(line1),
      line2: safeTrim(selectedResidentialAddress.addressLine2),
      line3: safeTrim(selectedResidentialAddress.townCity),
      line4: safeTrim(sanitizedLine4),
      postcode: safeTrim(selectedResidentialAddress.postCode),
      countryCode: safeTrim(selectedResidentialAddress.country),
      defaultDeliveryAddress,
      defaultBillingAddress,
    };
  },
);

export const getUsedDeliveryAddressOnPaymentResolution = createSelector(
  [getUsedDeliveryAddressOnPaymentResolutionPage, getSections,
    getShouldPassNameCodeForStateIfAvailableOnPaymentResolutionPage,
    getShouldRemoveSelectKeyword],
  (usedDeliveryAddressOnPaymentResolutionPage, sections,
   ShouldPassNameCodeForStateIfAvailableOnPaymentResolutionPage,
   shouldRemoveSelectKeyword) => {
    if (!usedDeliveryAddressOnPaymentResolutionPage) {
      return;
    }
    
    let addressee = (usedDeliveryAddressOnPaymentResolutionPage.firstName && usedDeliveryAddressOnPaymentResolutionPage.lastName)
      ? `${usedDeliveryAddressOnPaymentResolutionPage.firstName} ${usedDeliveryAddressOnPaymentResolutionPage.lastName}`
      : usedDeliveryAddressOnPaymentResolutionPage.addressee;
    
    if (addressee?.trim()?.length > 34) {
      addressee = addressee.substring(0, 34);
    }
    
    const addressType = sections?.includes(Sections.CONTACT) ? 'contact-details' : 'postal';
    
    const isPackstationAddress = getIsPackStationOrder(usedDeliveryAddressOnPaymentResolutionPage);
    
    let line1 = (isPackstationAddress && usedDeliveryAddressOnPaymentResolutionPage.line1 && usedDeliveryAddressOnPaymentResolutionPage.buildingNameNumber)
      ? `${usedDeliveryAddressOnPaymentResolutionPage.line1} ${usedDeliveryAddressOnPaymentResolutionPage.buildingNameNumber}`
      : usedDeliveryAddressOnPaymentResolutionPage.line1;
    
    if (isPackstationAddress && line1?.trim()?.length > 34) {
      line1 = line1.substring(0, 34);
    }
    
    const selectedState = usedDeliveryAddressOnPaymentResolutionPage.line4;
    let nameCodeToSet = '';
    
    if (ShouldPassNameCodeForStateIfAvailableOnPaymentResolutionPage) {
      const states = statesByField?.county(usedDeliveryAddressOnPaymentResolutionPage.countryCode);
      const stateFound = states?.find(field => field.name === selectedState);
      if (stateFound) {
        nameCodeToSet = stateFound.nameCode;
      }
    }
    
    const line4 = (ShouldPassNameCodeForStateIfAvailableOnPaymentResolutionPage && nameCodeToSet) ? nameCodeToSet : usedDeliveryAddressOnPaymentResolutionPage.line4;
    const sanitizedLine4 = shouldRemoveSelectKeyword ? removeSelectKeyword(line4) : line4;
    
    const defaultDeliveryAddress = usedDeliveryAddressOnPaymentResolutionPage.defaultDeliveryAddress;
    const defaultBillingAddress = usedDeliveryAddressOnPaymentResolutionPage.defaultBillingAddress;
    
    return {
      addressType,
      addressNickname: undefined,
      addressee: safeTrim(addressee),
      organisationName: safeTrim(usedDeliveryAddressOnPaymentResolutionPage.organisationName),
      buildingNameNumber: safeTrim(isPackstationAddress ? usedDeliveryAddressOnPaymentResolutionPage.postalId : usedDeliveryAddressOnPaymentResolutionPage.buildingNameNumber),
      line1: safeTrim(line1),
      line2: safeTrim(usedDeliveryAddressOnPaymentResolutionPage.line2),
      line3: safeTrim(usedDeliveryAddressOnPaymentResolutionPage.line3),
      line4: safeTrim(sanitizedLine4),
      postcode: safeTrim(usedDeliveryAddressOnPaymentResolutionPage.postcode),
      countryCode: safeTrim(usedDeliveryAddressOnPaymentResolutionPage.countryCode),
      defaultDeliveryAddress,
      defaultBillingAddress,
    };
  },
);

export const getPlaceOrderDeliveryAddress = (state) => {
  const deliveryType = getDeliveryType(state);
  if (deliveryType === 'pudo') {
    return getPudoDeliveryAddress(state);
  }
  return getResidentialDeliveryAddress(state);
};

export const getPlaceOrderDeliveryAddressOnPaymentResolution = (state) => {
  const deliveryType = getDeliveryType(state);
  if (deliveryType === 'pudo') {
    return getPudoDeliveryAddress(state);
  }
  return getUsedDeliveryAddressOnPaymentResolution(state);
};

export const getDifferentBillingAddress = state => state.address.billingAddress.values;

export const getBillingAddress = createSelector(
  [isUsingDifferentBillingAddress, getDifferentBillingAddress, getNewCardDetails, getPlaceOrderDeliveryAddress],
  (useDifferentAddressForBilling, differentBillingAddress, newCardDetails, deliveryAddress) => {
    if (useDifferentAddressForBilling) {
      return {
        addressType: 'postal',
        addressee: newCardDetails.nameOnCard,
        
        organisationName: differentBillingAddress.companyName,
        buildingNameNumber: differentBillingAddress.nameNumber,
        line1: differentBillingAddress.streetName,
        line2: differentBillingAddress.addressLine2,
        line3: differentBillingAddress.townCity,
        line4: differentBillingAddress.county,
        postcode: differentBillingAddress.postCode,
        countryCode: differentBillingAddress.country,
      };
    }
    return deliveryAddress;
  },
);

export const getOrderNewCardDetails = createSelector(
  [getNewCardDetails, getBillingAddress],
  (newCardDetails, billingAddress) => ({
    creditCardNumber: newCardDetails.creditCardNumber,
    nameOnCard: newCardDetails.nameOnCard,
    expirationMonth: newCardDetails.expiryDate.expirationMonth || newCardDetails.expiryDate.split('/')[0],
    expirationYear: newCardDetails.expiryDate.expirationYear || `20${newCardDetails.expiryDate.split('/')[1]}`,
    cv2: newCardDetails.cv2,
    method: 'new-credit-card',
    billingAddress,
  }),
);

export const getOrderStoredCardDetails = createSelector(
  [getSelectedPaymentOption, getStoredCV2Details],
  (selectedPaymentOption, storedCV2Details) => {
    const idHash = selectedPaymentOption.displayId;
    
    const storedCardID = Number(idHash) - 1000;
    const cv2 = storedCV2Details[`card${storedCardID}-cv2.storedCardCV2`];
    
    return {method: 'stored-credit-card', idHash, cv2};
  },
);

export const getKlarnaDateOfBirth = createSelector(
  [getLegacyKlarnaDetails],
  (klarnaDetails) => {
    if (!klarnaDetails || !klarnaDetails.dob || !klarnaDetails.dob.dobYear) return '';
    
    const {dobDay, dobMonth, dobYear} = klarnaDetails.dob;
    
    return `${dobYear}-${dobMonth}-${dobDay}`;
  },
);

export const getGooglePayOrderBillingAddress = createSelector(
  [getGooglePayBillingAddress],
  googlePayBillingAddress => ({
    addressType: 'postal',
    line1: googlePayBillingAddress.address1,
    line2: googlePayBillingAddress.address2,
    line3: googlePayBillingAddress.address3,
    line4: `${googlePayBillingAddress.locality} `
      + `${googlePayBillingAddress.administrativeArea} ${googlePayBillingAddress.sortingCode}`,
    addressee: googlePayBillingAddress.name,
    postcode: googlePayBillingAddress.postalCode,
    countryCode: googlePayBillingAddress.countryCode,
  }),
);

export const getGooglePayData = createSelector(
  [getGooglePayPaymentData],
  googlePayPaymentData => ({
    method: 'googlepay',
    description: googlePayPaymentData.paymentMethodData.description,
    billingAddress: getGooglePayOrderBillingAddress(store.getState()),
    token: googlePayPaymentData.paymentMethodData.tokenizationData.token,
    tokenizationType: googlePayPaymentData.paymentMethodData.tokenizationData.type,
    type: googlePayPaymentData.paymentMethodData.type,
    cardNetwork: googlePayPaymentData.paymentMethodData.info.cardNetwork,
  }),
);

export const getKlarnaData = createSelector(
  [getLegacyKlarnaDetails, getKlarnaDateOfBirth, getSelectedDeliveryCountry],
  (klarnaDetails, dateOfBirth, countryCode) => ({
    firstName: klarnaDetails.firstName,
    lastName: klarnaDetails.lastName,
    personalNumber: klarnaDetails.personalNumber,
    gender: klarnaDetails.gender,
    dateOfBirth,
    countryCode,
    method: 'klarna',
  }),
);

export const getArvatoData = createSelector(
  [getArvatoPaymentData, getArvatoPersonalDetails, getPlaceOrderBillingAddressDetails],
  (arvatoPaymentData, personalDetails, billingAddress) => ({
    ...arvatoPaymentData,
    ...personalDetails,
    billingAddress,
  }),
);

export const getPaymentName = createSelector(
  [getSelectedPaymentOption],
  (selectedPaymentOption) => {
    if (selectedPaymentOption.name.startsWith('ADYEN-')) {
      return selectedPaymentOption.name.replace('ADYEN', 'adyen');
    }
    if (selectedPaymentOption.name === 'gift_card') {
      return selectedPaymentOption.name;
    }
    return snakeToKebabCase(selectedPaymentOption.name);
  },
);

const getResidentIDDetails = state => state.address.deliveryAddressList.residentID;

export const getResidentID = createSelector(
  [getResidentIDDetails, requiresResidentIdOptionSelected,
    isResidentIDRequiredInAddressForm, getSelectedResidentialAddress],
  (residentIDDetails,
    residentIdRequiredOptionSelected, residentIDRequiredInAddressForm, selectedResidentialAddress) => {
    const isResidentIDRequired = residentIdRequiredOptionSelected || residentIDRequiredInAddressForm;
    
    if (isResidentIDRequired) {
      // If the resident has a stored ID and they want to use it, tell API not to use a new one
      if (residentIDDetails.useStoredID && selectedResidentialAddress.residentId) {
        return null;
      }
      
      return residentIDDetails.newResidentID;
    }
  },
);

export const getOrderEmailAddress = createSelector(
  [isGuest, getEmailAddress],
  (guest, emailAddress) => {
    if (guest) {
      return emailAddress;
    }
  },
);

export const isOrderOptedIntoMarketing = createSelector(
  [isGuest, isOptedIntoMarketing],
  (guest, optedIntoMarketing) => {
    if (guest) return optedIntoMarketing;
  },
);
